import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import { apiService } from '../../services/apiService';

interface Template {
  key: string;
  label: string;
  title: string;
  body: string;
  segment: string;
  segmentConfig: any;
}

interface TemplateSelectorProps {
  selectedKey: string | null;
  onSelect: (template: Template) => void;
}

const SEGMENT_COLORS: Record<string, string> = {
  ALL_USERS: '#2196f3',
  ABANDONED_CHECKOUT: '#ff9800',
  INACTIVE_USERS: '#9c27b0',
  UPCOMING_CLASS_TODAY: '#4caf50',
  UPCOMING_EVENT_TODAY: '#00bcd4',
  NEW_USERS: '#f44336',
};

const TemplateSelector: React.FC<TemplateSelectorProps> = ({ selectedKey, onSelect }) => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiService
      .getNotificationTemplates()
      .then((res: any) => {
        setTemplates(Array.isArray(res) ? res : res.data || []);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Grid container spacing={1.5}>
      {templates.map((template) => {
        const isSelected = selectedKey === template.key;
        const color = SEGMENT_COLORS[template.segment] || '#666';

        return (
          <Grid size={{ xs: 12, sm: 6 }} key={template.key}>
            <Card
              variant="outlined"
              sx={{
                border: isSelected ? `2px solid ${color}` : '1px solid rgba(0,0,0,0.12)',
                bgcolor: isSelected ? `${color}08` : 'background.paper',
                transition: 'all 0.2s',
                '&:hover': { borderColor: color, bgcolor: `${color}05` },
              }}
            >
              <CardActionArea onClick={() => onSelect(template)} sx={{ p: 0 }}>
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" fontWeight={600} sx={{ flex: 1, pr: 1 }}>
                      {template.label}
                    </Typography>
                    {isSelected && <CheckCircle sx={{ fontSize: 18, color }} />}
                  </Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {template.body}
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <Chip
                      label={template.segment.replace(/_/g, ' ')}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: '0.6rem',
                        bgcolor: `${color}18`,
                        color,
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default TemplateSelector;
